// PATH: src/pages/ChaptersPage.tsx
import * as React from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import RefreshIcon from "@mui/icons-material/Refresh";

import { useCurriculumApi } from "../api/curriculum/curriculum";
import { useDocumentsApi, type ChapterSourcePart } from "../api/documents/documents";
import { useObjectivesApi } from "../api/objectives/objectives";
import { useKnowledgeApi, type KnowledgeRunStatus } from "../api/knowledge/knowledge";
import { useCoverageApi, type OssatureChapterItem } from "../api/coverage/coverage";
import type { CurriculumProgram, Document } from "../models/domain";
import { useAuth } from "../context/AuthContext";
import { EmptyState, PageHeader, StatusPill, errorMessage, useConfirm, useToast } from "../components/ui";
import ChapterFilterBar from "../components/chapters/ChapterFilterBar";
import ChaptersTable, { type ChapterRow } from "../components/chapters/ChaptersTable";
import { useActivation } from "../components/activation/useActivation";
import ChapterDrawer, { type ChapterTab } from "../components/chapters/ChapterDrawer";
import ChapterImportDialog from "../components/documents/ChapterImportDialog";
import {
  COMMON_TRACK,
  buildListParams,
  curriculumNames,
  filterChapters,
  filtersFromParams,
  filtersToParams,
  findGrade,
  findProgram,
  gradeTracks,
  normalizeFilters,
  readStoredProgram,
  sameFilters,
  storeProgram,
  type ChapterFilters,
} from "../components/chapters/chapterFilters";
import {
  ISSUE_FILTER_LABELS,
  completenessByDocument,
  documentIssues,
  issueTotals,
} from "../components/coverage/chapterIssues";

/** Délai avant de relire la liste après une ré-extraction lancée en arrière-plan. */
const RELOAD_AFTER_RUN_MS = 6_000;

/**
 * Chapitres : « ce chapitre est-il bon ? ». Liste filtrée dans l'ordre du
 * programme, détail dans un panneau (objectifs et faits, leçons,
 * informations), import d'un chapitre (Markdown + manifeste).
 */
const ChaptersPage: React.FC = () => {
  const { getCurriculumOverview } = useCurriculumApi();
  const { listDocuments, getDocument, deleteDocument, downloadChapterSource } = useDocumentsApi();
  const { runBulkObjectives } = useObjectivesApi();
  const { runBulk: runBulkKnowledge, getRunStatus } = useKnowledgeApi();
  const { listOssatureChapters } = useCoverageApi();
  const toast = useToast();
  const confirm = useConfirm();
  // Relecteur : lecture et relecture seulement (ni import, ni extraction, ni suppression).
  const { canEdit } = useAuth();

  const [searchParams, setSearchParams] = useSearchParams();

  /* ---------- Catalogue ---------- */
  const [programs, setPrograms] = React.useState<CurriculumProgram[] | null>(null);
  const [catalogError, setCatalogError] = React.useState<string | null>(null);

  const loadCatalog = React.useCallback(() => {
    setCatalogError(null);
    getCurriculumOverview()
      .then(({ data }) => setPrograms(data.programs ?? []))
      .catch((e) => setCatalogError(errorMessage(e, "Impossible de charger le référentiel.")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  React.useEffect(loadCatalog, [loadCatalog]);

  /* ---------- Filtres ↔ URL ---------- */
  const rawFilters = React.useMemo(() => filtersFromParams(searchParams), [searchParams]);
  const filters = React.useMemo<ChapterFilters>(
    () => (programs ? normalizeFilters(rawFilters, programs, readStoredProgram()) : rawFilters),
    [rawFilters, programs],
  );

  // Programme par défaut, valeurs périmées : l'URL est réécrite sans entrée d'historique.
  React.useEffect(() => {
    if (!programs) return;
    if (!sameFilters(rawFilters, filters)) {
      setSearchParams(filtersToParams(filters, searchParams), { replace: true });
    }
    if (filters.program) storeProgram(filters.program);
  }, [programs, rawFilters, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateFilters = (patch: Partial<ChapterFilters>) => {
    const next = programs ? normalizeFilters({ ...filters, ...patch }, programs) : { ...filters, ...patch };
    // `q` tapé : pas d'entrée d'historique à chaque frappe.
    setSearchParams(filtersToParams(next, searchParams), { replace: Object.keys(patch).every((k) => k === "q") });
  };

  const program = programs ? findProgram(programs, filters.program) : undefined;
  const names = React.useMemo(() => curriculumNames(program), [program]);

  /* ---------- Liste ---------- */
  const [rows, setRows] = React.useState<ChapterRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [listError, setListError] = React.useState<string | null>(null);
  const listKey = JSON.stringify(filters.program ? buildListParams(filters) : null);
  const requestSeq = React.useRef(0);

  const fetchRows = React.useCallback(async () => {
    if (!filters.program) return;
    const seq = ++requestSeq.current;
    setLoading(true);
    setListError(null);
    try {
      const { data } = await listDocuments(buildListParams(filters));
      if (seq !== requestSeq.current) return;
      setRows(data as ChapterRow[]);
    } catch (e) {
      if (seq !== requestSeq.current) return;
      setListError(errorMessage(e, "Impossible de charger les chapitres."));
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listKey]);

  React.useEffect(() => {
    void fetchRows();
  }, [fetchRows]);

  /* ---------- Complétude (défauts de chaque chapitre) ---------- */
  /* Une seule requête pour tout le programme, jointe par document. En échec
     ou sur un serveur ancien, la liste marche comme avant. */
  const [completeness, setCompleteness] = React.useState<Map<string, OssatureChapterItem> | null>(null);
  const [completenessState, setCompletenessState] = React.useState<"loading" | "ok" | "error">("loading");
  const [measured, setMeasured] = React.useState<{ lessons: boolean } | null>(null);
  const completenessSeq = React.useRef(0);

  const fetchCompleteness = React.useCallback(async () => {
    if (!filters.program) return;
    const seq = ++completenessSeq.current;
    setCompletenessState((s) => (s === "ok" ? s : "loading"));
    try {
      const { data } = await listOssatureChapters({ program_id: filters.program });
      if (seq !== completenessSeq.current) return;
      const items = data?.items ?? [];
      setCompleteness(completenessByDocument(items));
      setMeasured(issueTotals(items).measured);
      setCompletenessState("ok");
    } catch {
      if (seq !== completenessSeq.current) return;
      setCompleteness(null);
      setMeasured(null);
      setCompletenessState("error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.program]);

  React.useEffect(() => {
    setCompleteness(null);
    setCompletenessState("loading");
    void fetchCompleteness();
  }, [fetchCompleteness]);

  const refreshAll = () => {
    void fetchRows();
    void fetchCompleteness();
  };
  // Pour un rechargement différé : toujours les filtres du moment, pas ceux du clic.
  const refreshAllRef = React.useRef(refreshAll);
  refreshAllRef.current = refreshAll;

  const issuesOf = React.useCallback(
    (r: ChapterRow) => documentIssues(r, completeness?.get(r.id)),
    [completeness],
  );
  const visible = React.useMemo(() => filterChapters(rows, filters, issuesOf), [rows, filters, issuesOf]);
  // Filtre de défauts sans défauts encore connus : la liste attend, plutôt que d'afficher « aucun ».
  const listLoading = loading || (filters.issue !== "all" && completenessState === "loading");

  const entry = findGrade(program, filters.grade);
  const columns = {
    grade: !filters.grade,
    subject: !filters.subject,
    track:
      filters.track !== COMMON_TRACK &&
      (gradeTracks(entry).length > 0 || (!filters.grade && rows.some((r) => r.track_id))),
  };

  /* ---------- Panneau de détail (`doc` dans l'URL) ---------- */
  const docId = searchParams.get("doc");
  const [tab, setTab] = React.useState<ChapterTab>(canEdit ? "objectives" : "review");
  const [fetchedDoc, setFetchedDoc] = React.useState<Document | null>(null);
  const [docNotFound, setDocNotFound] = React.useState(false);

  const rowDoc = docId ? rows.find((r) => r.id === docId) ?? null : null;
  const selected: Document | null = rowDoc ?? (fetchedDoc && fetchedDoc.id === docId ? fetchedDoc : null);

  // Lien vers un chapitre hors de la liste chargée : on le lit directement.
  React.useEffect(() => {
    setDocNotFound(false);
    if (!docId || loading || rowDoc || (fetchedDoc && fetchedDoc.id === docId)) return;
    let cancelled = false;
    getDocument(docId)
      .then(({ data }) => !cancelled && setFetchedDoc(data))
      .catch(() => !cancelled && setDocNotFound(true));
    return () => {
      cancelled = true;
    };
  }, [docId, loading, rowDoc]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDoc = (doc: Document, nextTab: ChapterTab = "objectives") => {
    setTab(nextTab);
    if (!rows.some((r) => r.id === doc.id)) setFetchedDoc(doc);
    const next = new URLSearchParams(searchParams);
    next.set("doc", doc.id);
    setSearchParams(next);
  };

  const closeDoc = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("doc");
    // Fermer ne crée pas d'entrée d'historique : « Retour » ne rouvre pas le panneau.
    setSearchParams(next, { replace: true });
    setFetchedDoc(null);
  };

  const handleChanged = (doc: Document) => {
    setRows((prev) => prev.map((r) => (r.id === doc.id ? { ...r, ...doc } : r)));
    if (fetchedDoc?.id === doc.id) setFetchedDoc({ ...fetchedDoc, ...doc });
  };

  /* ---------- Actions de ligne ---------- */
  const { toggleDocument } = useActivation();
  const [togglingId, setTogglingId] = React.useState<string | null>(null);
  const handleToggleActive = async (row: ChapterRow) => {
    setTogglingId(row.id);
    try {
      const state = await toggleDocument(row, !row.is_active);
      if (state) handleChanged({ ...row, is_active: state.is_active, subject_active: state.subject_active });
    } finally {
      setTogglingId(null);
    }
  };

  const handleDownload = async (doc: Document, part: ChapterSourcePart) => {
    try {
      await downloadChapterSource(doc, part);
    } catch (e) {
      toast.error(
        part === "manifest" ? "Le manifeste de ce chapitre est introuvable." : "Le Markdown de ce chapitre est introuvable.",
      );
    }
  };

  const handleDelete = async (doc: Document) => {
    const ok = await confirm({
      title: "Supprimer ce chapitre ?",
      message: `« ${doc.title} » et son contenu importé (versions, objectifs, bases de faits) seront supprimés définitivement. La progression des élèves sur ses objectifs ne sera plus rattachée à rien ; pour corriger un chapitre, réimportez-le plutôt.`,
      confirmLabel: "Supprimer",
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteDocument(doc.id);
      toast.success("Chapitre supprimé.");
      if (docId === doc.id) closeDoc();
      setRows((prev) => prev.filter((r) => r.id !== doc.id));
    } catch (e) {
      toast.error(errorMessage(e, "Échec de la suppression."));
    }
  };

  /* ---------- Opérations ---------- */
  const [opsAnchor, setOpsAnchor] = React.useState<HTMLElement | null>(null);
  const [importOpen, setImportOpen] = React.useState(false);
  const [objectivesRunning, setObjectivesRunning] = React.useState(false);
  const [knowledgeStarting, setKnowledgeStarting] = React.useState(false);
  /* État du run du parc : sans lui, deux clics lancent deux runs concurrents
     et l'écran ne dit jamais où en est le premier. */
  const [knowledgeRun, setKnowledgeRun] = React.useState<KnowledgeRunStatus | null>(null);

  /* L'état du run n'est pas une information critique : une route absente ou
     un réseau qui tousse laisse simplement le badge vide. */
  const refreshKnowledgeRun = React.useCallback(async () => {
    if (!canEdit) return;
    try {
      setKnowledgeRun(await getRunStatus());
    } catch (err) {
      console.error(err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit]);

  React.useEffect(() => {
    void refreshKnowledgeRun();
  }, [refreshKnowledgeRun]);

  // Pendant le run seulement : « n documents sur N » sans recharger la page.
  React.useEffect(() => {
    if (!knowledgeRun?.running) return;
    const timer = window.setInterval(() => void refreshKnowledgeRun(), 10_000);
    return () => window.clearInterval(timer);
  }, [knowledgeRun?.running, refreshKnowledgeRun]);

  const handleRunObjectives = async () => {
    setOpsAnchor(null);
    if (!visible.length) return;
    const ok = await confirm({
      title: `Extraire les objectifs de ${visible.length} chapitre(s) ?`,
      message:
        "L'extraction tourne en arrière-plan, chapitre par chapitre. Ceux dont les objectifs sont déjà extraits " +
        "sont sautés (rien n'est remplacé).",
      confirmLabel: "Lancer l'extraction",
    });
    if (!ok) return;
    setObjectivesRunning(true);
    try {
      const res = await runBulkObjectives({ documents: visible.map((d) => d.id) });
      toast.success(`${res.data.count} extraction(s) lancée(s) en arrière-plan.`);
    } catch (e: any) {
      toast.error(
        e?.response?.status === 409
          ? "Une extraction d'objectifs est déjà en cours : attendez qu'elle finisse."
          : errorMessage(e, "Erreur lors du lancement de l'extraction des objectifs."),
      );
    } finally {
      setObjectivesRunning(false);
    }
  };

  /* Cibler : ré-extraire (force) les objectifs des chapitres à compléter
     affichés. Un objectif qui garde le même sens dans sa leçon garde son identifiant. */
  const [targetRunning, setTargetRunning] = React.useState(false);
  const reloadTimer = React.useRef<number | null>(null);
  React.useEffect(() => () => {
    if (reloadTimer.current) window.clearTimeout(reloadTimer.current);
  }, []);

  const handleRunTargeted = async () => {
    if (!visible.length) return;
    const n = visible.length;
    const ok = await confirm({
      title: `Ré-extraire les objectifs de ${n} chapitre${n > 1 ? "s" : ""} ?`,
      message:
        `Les objectifs des ${n} chapitre${n > 1 ? "s" : ""} affiché${n > 1 ? "s" : ""} sont recalculés, même là où ils existent ` +
        "déjà. Coût : quelques centimes par chapitre.\n\n" +
        "La ré-extraction tourne en arrière-plan, chapitre par chapitre, et rechaîne l'extraction des faits. " +
        "Les objectifs sont déduits leçon par leçon ; ceux qui gardent le même sens dans leur leçon gardent leur " +
        "identifiant (la progression des élèves est conservée), les autres deviennent orphelins (progression perdue).\n\n" +
        "Si une extraction d'objectifs tourne déjà, celle-ci est refusée : attendez qu'elle finisse.",
      confirmLabel: `Ré-extraire ${n} chapitre${n > 1 ? "s" : ""}`,
    });
    if (!ok) return;
    setTargetRunning(true);
    try {
      const res = await runBulkObjectives({ documents: visible.map((d) => d.id), force: true });
      toast.success(
        `${res.data.count} ré-extraction(s) lancée(s) en arrière-plan. La liste se met à jour dans quelques secondes ; ` +
          "actualisez plus tard pour voir le résultat complet.",
      );
      if (reloadTimer.current) window.clearTimeout(reloadTimer.current);
      reloadTimer.current = window.setTimeout(() => refreshAllRef.current(), RELOAD_AFTER_RUN_MS);
    } catch (e: any) {
      toast.error(
        e?.response?.status === 409
          ? "Une extraction d'objectifs est déjà en cours : attendez qu'elle finisse, puis relancez."
          : errorMessage(e, "Erreur lors du lancement de la ré-extraction des objectifs."),
      );
    } finally {
      setTargetRunning(false);
    }
  };

  /* Ré-extraction du parc : longue, coûteuse, et seulement utile en `force` —
     sans lui, `source_hash` inchangé fait sauter tous les documents et le run
     ne remplace AUCUNE unité de l'ancienne extraction. */
  const handleRunKnowledge = async () => {
    setOpsAnchor(null);
    const ok = await confirm({
      title: "Ré-extraire les bases de faits du parc ?",
      message:
        "Ce run ne touche pas aux objectifs : il ré-extrait les bases de faits de TOUS les chapitres, même là où " +
        "une base existe déjà (c'est le seul moyen de remplacer celles de la version précédente).\n\n" +
        "Il tourne en arrière-plan pendant plusieurs heures et coûte de l'ordre de 60 € d'appels au modèle. " +
        "Les désactivations faites par la relecture sont refaites ; les corrections éditoriales dont le fait a été " +
        "renuméroté seront signalées « à revoir ».",
      confirmLabel: "Lancer sur tout le parc",
      danger: true,
      typeToConfirm: "PARC",
    });
    if (!ok) return;
    setKnowledgeStarting(true);
    try {
      const res = await runBulkKnowledge({ all: true, force: true });
      const count = res.data.count;
      toast.success(
        count != null
          ? `${count} ré-extraction(s) de base de faits lancée(s) en arrière-plan.`
          : res.data.message || "Ré-extraction des bases de faits lancée en arrière-plan.",
      );
      // Le badge apparaît tout de suite, sans attendre le tour de 10 s.
      setKnowledgeRun({ running: true, total: count });
      void refreshKnowledgeRun();
    } catch (e: any) {
      if (e?.response?.status === 409) {
        toast.warning("Un run du parc est déjà en cours : deux runs concurrents se bloqueraient l'un l'autre.");
        void refreshKnowledgeRun();
      } else {
        toast.error(errorMessage(e, "Erreur lors du lancement de la ré-extraction."));
      }
    } finally {
      setKnowledgeStarting(false);
    }
  };

  const runBadge = knowledgeRun?.running ? (
    <Tooltip
      title={
        "Ré-extraction des bases de faits du parc en cours" +
        (knowledgeRun.started_at ? ` depuis ${new Date(knowledgeRun.started_at).toLocaleString("fr-FR")}` : "") +
        (knowledgeRun.failed ? ` · ${knowledgeRun.failed} chapitre(s) en échec` : "")
      }
    >
      <span>
        <StatusPill tone="info">
          {knowledgeRun.total != null
            ? `Bases de faits : ${knowledgeRun.done ?? 0}/${knowledgeRun.total}`
            : "Bases de faits : run en cours"}
        </StatusPill>
      </span>
    </Tooltip>
  ) : null;

  /* ---------- Rendu ---------- */
  const hasClientFilter =
    Boolean(filters.q.trim()) ||
    filters.state !== "all" ||
    filters.issue !== "all" ||
    filters.review !== "all" ||
    filters.track === COMMON_TRACK;
  const hasAnyFilter = hasClientFilter || Boolean(filters.grade || filters.subject || filters.track);

  const renderList = () => {
    if (catalogError) {
      return (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={loadCatalog}>
              Réessayer
            </Button>
          }
        >
          {catalogError}
        </Alert>
      );
    }
    if (programs && programs.length === 0) {
      return (
        <Paper variant="outlined">
          <EmptyState
            icon={<MenuBookOutlinedIcon />}
            title="Aucun programme"
            description="Créez d'abord un programme dans le catalogue : chaque chapitre y est rattaché."
          />
        </Paper>
      );
    }
    if (listError) {
      return (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void fetchRows()}>
              Réessayer
            </Button>
          }
        >
          {listError}
        </Alert>
      );
    }
    if (!listLoading && programs && visible.length === 0) {
      return (
        <Paper variant="outlined">
          {rows.length > 0 || hasAnyFilter ? (
            <EmptyState
              icon={<SearchOffIcon />}
              title="Aucun chapitre ne correspond"
              description={canEdit ? "Élargissez les filtres, ou importez le chapitre qui manque." : "Élargissez les filtres."}
              action={
                <Stack direction="row" spacing={1} justifyContent="center">
                  <Button
                    variant="outlined"
                    onClick={() =>
                      updateFilters({ grade: "", subject: "", track: "", q: "", state: "all", issue: "all", review: "all" })
                    }
                  >
                    Effacer les filtres
                  </Button>
                  {canEdit && (
                    <Button variant="contained" startIcon={<UploadFileIcon />} onClick={() => setImportOpen(true)}>
                      Importer un chapitre
                    </Button>
                  )}
                </Stack>
              }
            />
          ) : (
            <EmptyState
              icon={<MenuBookOutlinedIcon />}
              title="Aucun chapitre dans ce programme"
              description={
                canEdit
                  ? "Un chapitre s'importe avec deux fichiers : le Markdown du cours et son manifeste JSON."
                  : "Aucun chapitre n'a encore été importé dans ce programme."
              }
              action={
                canEdit ? (
                  <Button variant="contained" startIcon={<UploadFileIcon />} onClick={() => setImportOpen(true)}>
                    Importer un chapitre
                  </Button>
                ) : undefined
              }
            />
          )}
        </Paper>
      );
    }
    return (
      <ChaptersTable
        rows={visible}
        loading={listLoading || !programs}
        columns={columns}
        names={names}
        selectedId={docId}
        completeness={completeness}
        // Liste filtrée par relecture, ou relecteur : le chapitre s'ouvre sur sa relecture.
        onOpen={(row) => openDoc(row, !canEdit || filters.review !== "all" ? "review" : "objectives")}
        onOpenReview={(row) => openDoc(row, "review")}
        onEdit={(row) => openDoc(row, "info")}
        onDownload={(row, part) => void handleDownload(row, part)}
        onDelete={(row) => void handleDelete(row)}
        onToggleActive={(row) => void handleToggleActive(row)}
        togglingId={togglingId}
        readOnly={!canEdit}
      />
    );
  };

  return (
    <Box>
      <PageHeader
        title="Chapitres"
        subtitle={
          canEdit
            ? "Les chapitres importés, dans l'ordre du programme : contrôlez leurs leçons, leurs objectifs et leurs bases de faits."
            : "Les chapitres importés, dans l'ordre du programme : relisez-les, puis validez ou signalez ce qui est à corriger (onglet Relecture)."
        }
        actions={
          <>
            {runBadge}
            <Tooltip title="Actualiser la liste et les défauts">
              <span>
                <IconButton
                  onClick={refreshAll}
                  disabled={loading || !filters.program}
                  aria-label="Actualiser"
                  sx={{ border: 1, borderColor: "divider", bgcolor: "background.paper" }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {canEdit && (
              <>
                <Button
                  variant="outlined"
                  startIcon={objectivesRunning || knowledgeStarting ? <CircularProgress size={14} /> : <MoreVertIcon />}
                  onClick={(e) => setOpsAnchor(e.currentTarget)}
                  aria-haspopup="menu"
                >
                  Opérations
                </Button>
                <Button variant="contained" startIcon={<UploadFileIcon />} onClick={() => setImportOpen(true)}>
                  Importer un chapitre
                </Button>
              </>
            )}
          </>
        }
      >
        {programs ? (
          <ChapterFilterBar programs={programs} filters={filters} onChange={updateFilters} measured={measured} />
        ) : catalogError ? null : (
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} variant="rounded" width={200} height={40} />
            ))}
          </Stack>
        )}
      </PageHeader>

      {!listLoading && programs && rows.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {visible.length === rows.length
            ? `${rows.length} chapitre${rows.length > 1 ? "s" : ""}`
            : `${visible.length} chapitre${visible.length > 1 ? "s" : ""} sur ${rows.length}`}
          {program && programs.length > 1 ? ` · ${program.program.name}` : ""}
        </Typography>
      )}

      {filters.issue !== "all" && completenessState === "error" && (
        <Alert
          severity="warning"
          sx={{ mb: 1.5 }}
          action={
            <Button color="inherit" size="small" onClick={() => void fetchCompleteness()}>
              Réessayer
            </Button>
          }
        >
          Les défauts des chapitres n'ont pas pu être chargés : le filtre « {ISSUE_FILTER_LABELS[filters.issue]} » ne
          peut pas s'appliquer.
        </Alert>
      )}

      {filters.issue !== "all" && !loading && completenessState === "ok" && visible.length > 0 && (
        <Paper
          variant="outlined"
          sx={{
            mb: 1.5,
            px: 2,
            py: 1.25,
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "warning.light",
            borderColor: "warning.main",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2">
              {visible.length} chapitre{visible.length > 1 ? "s" : ""} à compléter
              {filters.issue !== "any" ? ` · ${ISSUE_FILTER_LABELS[filters.issue]}` : ""}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {!canEdit
                ? "Signalez-les dans l'onglet Relecture du chapitre (« À corriger »)."
                : filters.issue === "inactive"
                ? "Un chapitre s'active depuis sa fiche, onglet Informations."
                : "Ré-extraire leurs objectifs, leçon par leçon, comble souvent les leçons vides ou faibles et rechaîne les faits."}
            </Typography>
          </Box>
          {canEdit && filters.issue !== "inactive" && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => void handleRunTargeted()}
              disabled={targetRunning}
              startIcon={targetRunning ? <CircularProgress size={14} /> : <FormatListBulletedIcon />}
              sx={{ flexShrink: 0 }}
            >
              Ré-extraire leurs objectifs
            </Button>
          )}
        </Paper>
      )}

      {renderList()}

      <Menu
        anchorEl={opsAnchor}
        open={Boolean(opsAnchor)}
        onClose={() => setOpsAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { maxWidth: 380 } } }}
      >
        <MenuItem onClick={() => void handleRunObjectives()} disabled={objectivesRunning || visible.length === 0}>
          <ListItemIcon>
            <FormatListBulletedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Extraire les objectifs des chapitres affichés"
            secondary={`${visible.length} chapitre(s), ceux déjà extraits sont sautés`}
            secondaryTypographyProps={{ whiteSpace: "normal" }}
          />
        </MenuItem>
        <MenuItem onClick={() => void handleRunKnowledge()} disabled={knowledgeStarting || Boolean(knowledgeRun?.running)}>
          <ListItemIcon>
            <AccountTreeOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Ré-extraire les bases de faits du parc"
            secondary={knowledgeRun?.running ? "Un run est déjà en cours" : "Tous les programmes · coûteux (≈ 60 €)"}
            secondaryTypographyProps={{ whiteSpace: "normal" }}
          />
        </MenuItem>
      </Menu>

      {canEdit && (
        <ChapterImportDialog
          open={importOpen}
          onClose={() => setImportOpen(false)}
          onImported={() => void fetchRows()}
          onOpenChapter={(doc) => {
            setImportOpen(false);
            openDoc(doc);
          }}
          knownDocuments={rows}
          programs={programs ?? []}
        />
      )}

      <ChapterDrawer
        document={selected}
        loading={Boolean(docId) && !selected && !docNotFound}
        notFound={Boolean(docId) && docNotFound}
        tab={tab}
        onTabChange={setTab}
        names={names}
        onClose={closeDoc}
        onDownload={(doc, part) => void handleDownload(doc, part)}
        onChanged={handleChanged}
        onObjectivesExtracted={() => void fetchCompleteness()}
      />
    </Box>
  );
};

export default ChaptersPage;
